"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root as any

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = ({
  className,
  children,
  ...props
}: any) => (
  <DialogPrimitive.Portal className={cn(className)} {...props}>
    <div className="fixed inset-0 z-50 flex items-start justify-center">
      {children}
    </div>
  </DialogPrimitive.Portal>
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-[var(--surface-overlay)] backdrop-blur-sm transition-all duration-100 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  overlayClassName?: string
  overlayStyle?: React.CSSProperties
  contentWidth?: string
  contentHeight?: string
  contentMaxWidth?: string
  contentMaxHeight?: string
  contentTop?: string
  hideCloseButton?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      overlayClassName,
      overlayStyle,
      contentWidth,
      contentHeight,
      contentMaxWidth,
      contentMaxHeight,
      contentTop,
      hideCloseButton = false,
      onInteractOutside,
      style,
      ...props
    },
    ref
  ) => {
    const resolvedMaxWidth = contentMaxWidth || "80vw"
    const resolvedMaxHeight = contentMaxHeight || "80vh"
    const enforcedStyle = {
      ...style,
      width: contentWidth || "80vw",
      height: contentHeight || "80vh",
      maxWidth: resolvedMaxWidth,
      maxHeight: resolvedMaxHeight,
      top: contentTop || "5vh",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translateX(-50%)",
    }

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} style={overlayStyle} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed z-50 grid w-full gap-3 rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-[var(--panel-padding)] text-[var(--neutral-gray-120)] shadow-[var(--shadow-medium)]",
            className
          )}
          onInteractOutside={(event) => {
            event.preventDefault()
            if (onInteractOutside) {
              onInteractOutside(event)
            }
          }}
          style={enforcedStyle}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="absolute right-3 top-3 z-20 rounded-[var(--border-radius-small)] p-1 text-[var(--neutral-gray-80)] transition-colors hover:bg-[var(--surface-muted)] hover:text-[var(--neutral-gray-120)] focus:outline-none focus:ring-2 focus:ring-[var(--azure-focus-ring)] focus:ring-offset-2 focus:ring-offset-white disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  }
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col gap-2 sm:flex-row sm:justify-end sm:space-x-0",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-[var(--neutral-gray-120)]",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-[var(--neutral-gray-80)]", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
